import {useState, useEffect, useRef} from "react";
import {Amplify} from "aws-amplify";
import {
  signUp,
  confirmSignUp,
  signIn,
  signOut,
  getCurrentUser,
  fetchUserAttributes,
} from "aws-amplify/auth";
import {sessionStorage} from "aws-amplify/utils";
import {cognitoUserPoolsTokenProvider} from "aws-amplify/auth/cognito";
import {ShieldCheck, User} from "lucide-react";

import VideoDashboard from "./components/VideoDashboard";
import AdminDashboard from "./components/AdminDashboard";

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: process.env.REACT_APP_AWS_USER_POOL_ID,
      userPoolClientId: process.env.REACT_APP_AWS_USER_POOL_WEB_CLIENT_ID,
    },
  },
});

// Configure storage globally so Amplify knows where to look for tokens on initialization
cognitoUserPoolsTokenProvider.setKeyValueStorage(sessionStorage);

const OrbitLoader = () => (
  <div className="relative h-10 w-10 animate-spin">
    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-800 rounded-full"></div>
    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-800/50 rounded-full"></div>
    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-2 h-2 bg-gray-800/50 rounded-full"></div>
    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 bg-gray-800/50 rounded-full"></div>
  </div>
);

const App = () => {
  const [signUpData, setSignUpData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    password: "",
    confirm_password: "",
  });

  const [loginData, setLoginData] = useState({
    email: "",
    password: "",
  });

  const [confirmData, setConfirmData] = useState({
    email: "",
    confirmationCode: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [adminPageOpen, setAdminPageOpen] = useState(false);
  const dropdownRef = useRef(null);

  const [loginState, setLoginState] = useState({
    state: null,
    message: null,
    user: null,
  });

  // --- Click Away Listener for Dropdown ---
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // --- Handlers ---
  const handleSignUpChange = (e) => {
    const {id, value} = e.target;
    setSignUpData((prev) => ({...prev, [id]: value}));
  };

  const handleLoginChange = (e) => {
    const {id, value} = e.target;
    setLoginData((prev) => ({...prev, [id]: value}));
  };

  const handleConfirmChange = (e) => {
    const {id, value} = e.target;
    setConfirmData((prev) => ({...prev, [id]: value}));
  };

  // --- Logic ---
  const handleSignUp = async () => {
    if (signUpData.password !== signUpData.confirm_password) {
      setLoginState({
        state: "signup",
        message: "Passwords do not match.",
        user: null,
      });
      return;
    }

    setIsActionLoading(true);
    try {
      const {nextStep} = await signUp({
        username: signUpData.email,
        password: signUpData.password,
        options: {
          userAttributes: {
            given_name: signUpData.firstname,
            family_name: signUpData.lastname,
          },
          autoSignIn: true,
        },
      });

      if (nextStep.signUpStep === "CONFIRM_SIGN_UP") {
        setLoginState({
          state: "confirm",
          message: `Check your email for the confirmation code.`,
          user: null,
        });
        setConfirmData((prev) => ({...prev, email: signUpData.email}));
      }
    } catch (error) {
      setLoginState({state: "signup", message: error.message, user: null});
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleSignUpConfirmation = async () => {
    setIsActionLoading(true);
    try {
      const {nextStep} = await confirmSignUp({
        username: confirmData.email,
        confirmationCode: confirmData.confirmationCode,
      });
      if (
        nextStep.signUpStep === "DONE" ||
        nextStep.signUpStep === "COMPLETE_AUTO_SIGN_IN"
      ) {
        setLoginState({
          state: "login",
          message: "Account confirmed! Please log in.",
          user: null,
        });
      }
    } catch (error) {
      setLoginState({state: "confirm", message: error.message, user: null});
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleLogin = async () => {
    setIsActionLoading(true);
    try {
      const {isSignedIn, nextStep} = await signIn({
        username: loginData.email,
        password: loginData.password,
      });

      if (nextStep.signInStep === "CONFIRM_SIGN_UP") {
        setLoginState({
          state: "confirm",
          message: "Email not confirmed.",
          user: null,
        });
      } else if (isSignedIn || nextStep.signInStep === "DONE") {
        await currentAuthenticatedUser();
      }
    } catch (error) {
      setLoginState({
        state: "login",
        message: "Incorrect username or password.",
        user: null,
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  const currentAuthenticatedUser = async () => {
    setIsLoading(true);
    try {
      const user = await getCurrentUser();
      const attributes = await fetchUserAttributes();
      const formattedUser = {
        ...user,
        ...attributes,
      };
      if (user)
        setLoginState({state: "logged_in", user: formattedUser, message: null});
    } catch (err) {
      // Silence the error log for unauthenticated users as it is the expected state when not logged in
      setLoginState({state: "login", user: null, message: null});
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    setIsLoading(true);
    setIsDropdownOpen(false);
    try {
      await signOut();
      setLoginState({state: "login", message: null, user: null});
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to handle opening the admin page
  const handleAdminClick = () => {
    setIsDropdownOpen(false);
    setAdminPageOpen((prev) => !prev);
  };

  useEffect(() => {
    currentAuthenticatedUser();
  }, []);

  const isSignUpValid = Object.values(signUpData).every((val) => val !== "");
  const isLoginValid = loginData.email !== "" && loginData.password !== "";
  const isConfirmValid =
    confirmData.email !== "" && confirmData.confirmationCode !== "";

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      <header className="flex bg-white w-full h-[10%] items-center justify-between shadow-sm flex-shrink-0 z-50">
        <div className="flex">
          <img
            src="./images/ccx_logo.png"
            className="pointer-events-none p-4 w-40"
            alt="Logo"
          />
        </div>

        {loginState.state === "logged_in" && loginState.user && (
          <div className="relative mx-8" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center gap-3 focus:outline-none group"
            >
              <div className="flex flex-col items-end hidden sm:flex">
                <span className="text-sm font-bold text-gray-800">
                  {loginState.user?.signInDetails?.loginId?.toUpperCase()}
                </span>
                <span className="text-xs text-gray-500">Member</span>
              </div>
              <div className="w-10 h-10 bg-slate-300 rounded-full flex items-center justify-center text-white font-bold border-2 border-gray-400/50 transition-all">
                <User className="cursor-pointer text-white hover:text-gray-400" />
              </div>
            </button>

            {isDropdownOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 py-2 animate-in fade-in slide-in-from-top-2 duration-200">
                <div className="px-4 py-2 border-b border-gray-100">
                  <p className="text-xs text-gray-400 uppercase tracking-widest font-bold">
                    Account
                  </p>
                  <p className="text-sm text-gray-600 truncate">
                    {loginState.user?.signInDetails?.loginId}
                  </p>
                </div>
                {/* Admin Option */}
                {adminPageOpen ? (
                  <button
                    onClick={handleAdminClick}
                    className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-slate-50 flex items-center gap-2 transition-colors border-b border-gray-50"
                  >
                    <User size={16} className="text-slate-500" />
                    User Dashboard
                  </button>
                ) : (
                  <button
                    onClick={handleAdminClick}
                    className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-slate-50 flex items-center gap-2 transition-colors border-b border-gray-50"
                  >
                    <ShieldCheck size={16} className="text-slate-500" />
                    Admin Dashboard
                  </button>
                )}
                <button
                  onClick={handleSignOut}
                  className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 font-semibold transition-colors"
                >
                  Log Out
                </button>
              </div>
            )}
          </div>
        )}
      </header>

      <section className="flex flex-col h-[90%] w-full items-center justify-center overflow-y-auto bg-gray-100">
        {isLoading ? (
          <OrbitLoader />
        ) : (
          <>
            {(loginState.state === "signup" ||
              loginState.state === "login" ||
              loginState.state === "confirm") && (
              <div className="flex flex-col bg-white w-full max-w-md min-h-[50%] rounded-lg shadow-lg items-center justify-center p-8">
                {loginState.state === "signup" && (
                  <>
                    {[
                      "firstname",
                      "lastname",
                      "email",
                      "password",
                      "confirm_password",
                    ].map((key) => (
                      <div key={key} className="flex py-2 w-4/5">
                        <input
                          id={key}
                          type={key.includes("password") ? "password" : "text"}
                          placeholder={key.replace("_", " ").toUpperCase()}
                          value={signUpData[key]}
                          onChange={handleSignUpChange}
                          className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                      </div>
                    ))}
                    <div className="flex py-4 w-4/5 gap-4">
                      <button
                        onClick={handleSignUp}
                        disabled={!isSignUpValid || isActionLoading}
                        className="flex-1 bg-blue-600 text-white py-3 rounded font-bold disabled:bg-gray-400"
                      >
                        {isActionLoading ? "..." : "SIGN UP"}
                      </button>
                      <button
                        onClick={() =>
                          setLoginState({
                            state: "login",
                            message: null,
                            user: null,
                          })
                        }
                        className="flex-1 border border-blue-600 text-blue-600 py-3 rounded font-bold"
                      >
                        LOGIN
                      </button>
                    </div>
                  </>
                )}

                {loginState.state === "login" && (
                  <>
                    <div className="flex py-2 w-4/5">
                      <input
                        id="email"
                        type="email"
                        placeholder="Email"
                        value={loginData.email}
                        onChange={handleLoginChange}
                        className="w-full p-3 border border-gray-300 rounded outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="flex py-2 w-4/5">
                      <input
                        id="password"
                        type="password"
                        placeholder="Password"
                        value={loginData.password}
                        onChange={handleLoginChange}
                        className="w-full p-3 border border-gray-300 rounded outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="flex py-4 w-4/5">
                      <button
                        onClick={handleLogin}
                        disabled={!isLoginValid || isActionLoading}
                        className="w-full bg-blue-600 text-white py-3 rounded font-bold disabled:bg-gray-400"
                      >
                        {isActionLoading ? "..." : "LOGIN"}
                      </button>
                    </div>
                    <h5
                      onClick={() =>
                        setLoginState({
                          state: "signup",
                          message: null,
                          user: null,
                        })
                      }
                      className="text-blue-600 text-sm underline cursor-pointer"
                    >
                      Sign up for a new account
                    </h5>
                  </>
                )}

                {loginState.state === "confirm" && (
                  <>
                    <div className="flex py-2 w-4/5">
                      <input
                        id="email"
                        type="email"
                        placeholder="Email"
                        value={confirmData.email}
                        onChange={handleConfirmChange}
                        className="w-full p-3 border border-gray-300 rounded outline-none"
                      />
                    </div>
                    <div className="flex py-2 w-4/5">
                      <input
                        id="confirmationCode"
                        type="text"
                        placeholder="Code"
                        value={confirmData.confirmationCode}
                        onChange={handleConfirmChange}
                        className="w-full p-3 border border-gray-300 rounded outline-none"
                      />
                    </div>
                    <div className="flex py-4 w-4/5 gap-4">
                      <button
                        onClick={handleSignUpConfirmation}
                        disabled={!isConfirmValid || isActionLoading}
                        className="flex-1 bg-blue-600 text-white py-3 rounded font-bold"
                      >
                        CONFIRM
                      </button>
                    </div>
                  </>
                )}

                {loginState.message && (
                  <p className="text-red-600 text-sm mt-2 italic">
                    {loginState.message}
                  </p>
                )}
              </div>
            )}

            {loginState.state === "logged_in" &&
              (adminPageOpen ? (
                <AdminDashboard />
              ) : (
                <VideoDashboard user={loginState.user} />
              ))}
          </>
        )}
      </section>
    </div>
  );
};

export default App;
